import { useCallback, useRef } from "react";
import useDevicesStore from "../stores/devicesStore";
import useCredentialsStore from "../stores/credentialsStore";
import useUIStore from "../stores/uiStore";
import { useDeviceOperations } from "./useDeviceOperations";
import { getInstallationSyncCache } from "../utils/cacheManager";
import logger from "../utils/logger";
import config from "../config";

const COMMON_HEADERS = {
  "Content-Type": "application/json",
};

/**
 * Custom hook for managing data loading operations for devices
 * Extracts data loading orchestration from AppContent.js
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.installationId - Current installation ID
 * @param {Function} params.authenticatedFetch - Authenticated fetch wrapper
 * @param {Function} params.showNotification - Notification display function
 * @param {Function} params.setDevices - Function to set devices array
 * @param {Function} params.setSelectedDeviceId - Function to set selected device
 * @param {Function} params.setActionHistory - Function to set action history
 * @param {Array} params.devices - Current devices array
 * @param {Ref} params.selectedDeviceIdRef - Ref to selected device ID
 * @param {Function} params.getStatusStore - Function to get credential status from localStorage
 * @param {Function} params.setConfigError - Function to set configuration error state
 * @returns {Object} Data loader functions
 */
export const useDataLoader = ({
  installationId,
  authenticatedFetch,
  showNotification,
  setDevices,
  setSelectedDeviceId,
  setActionHistory,
  devices,
  selectedDeviceIdRef,
  getStatusStore,
  setConfigError,
}) => {
  const credentialsLoadStartTime = useRef(null);

  // Store accessors
  const { setCredentials, setCredentialsLoaded } = useCredentialsStore();
  const { setDeviceStates, updateDeviceState } = useDevicesStore();
  const { setSyncing, setLoading } = useUIStore();

  // Device operations (fetch functions)
  const { fetchDevices, fetchDeviceState } =
    useDeviceOperations(authenticatedFetch);

  /**
   * Load data for a specific device
   * Implements cache-first strategy with timestamp-based validation
   *
   * @param {string} deviceId - Device ID to load data for
   */
  const loadDataForDevice = useCallback(
    async (deviceId) => {
      logger.log(`=== [loadDataForDevice] START for device ${deviceId} ===`);

      if (!deviceId) {
        logger.log(`[loadDataForDevice] No deviceId provided, clearing state`);
        setCredentials([]);
        setDeviceStates({});
        setActionHistory([]); // Clear history if no device is selected
        return;
      }

      // Check if we already have cached data for this device (use store.getState() to avoid dependency)
      const cachedData = useDevicesStore.getState().deviceStates[deviceId];
      logger.log(`[loadDataForDevice] Cache check for ${deviceId}:`);
      logger.log(`[loadDataForDevice] - Cache exists: ${!!cachedData}`);
      logger.log(
        `[loadDataForDevice] - Cache timestamp: ${
          cachedData?.lastUpdated || "none"
        }`
      );
      logger.log(
        `[loadDataForDevice] - Cached credentials count: ${
          cachedData?.credentials?.length || 0
        }`
      );

      if (cachedData) {
        logger.log(`[loadDataForDevice] 🎯 Using cached data for ${deviceId}`);
        logger.log(
          `[loadDataForDevice] Cached credentials:`,
          cachedData.credentials?.map((c) => ({
            id: c.id,
            patronName: c.patronName,
          })) || []
        );
        const statusStore = getStatusStore();
        const mergedCreds = (cachedData.credentials || []).map((cred) => ({
          ...cred,
          status: cred.status || statusStore[cred.id] || "Active", // Prefer cached status, fall back to statusStore
        }));
        logger.log(
          `[loadDataForDevice] 🔄 Setting ${mergedCreds.length} cached credentials in UI`
        );
        setCredentials(mergedCreds);
        // Initialize history with the cached last action
        if (cachedData.lastAction) {
          // Ensure deviceId is embedded in the action
          setActionHistory([{ ...cachedData.lastAction, deviceId }]);
        } else {
          setActionHistory([]);
        }
        logger.log(
          `[loadDataForDevice] ✅ Cached credentials displayed (overlay remains until API refresh)`
        );
      } else {
        logger.log(`[loadDataForDevice] ⚠️  No cache found for ${deviceId}`);
      }

      setSyncing(true); // Show modal on device switch
      logger.log(`[loadDataForDevice] 📡 Fetching from API...`);
      try {
        const deviceData = await fetchDeviceState(deviceId);
        logger.log(`[loadDataForDevice] API response received`);
        logger.log(`[loadDataForDevice] - API data exists: ${!!deviceData}`);
        logger.log(
          `[loadDataForDevice] - API credentials count: ${
            deviceData?.credentials?.length || 0
          }`
        );
        logger.log(
          `[loadDataForDevice] - API timestamp: ${
            deviceData?.lastUpdated || "none"
          }`
        );

        if (deviceData) {
          logger.log(
            `[loadDataForDevice] API credentials:`,
            deviceData.credentials?.map((c) => ({
              id: c.id,
              patronName: c.patronName,
            })) || []
          );

          // Check if we should use API data or keep the cached data
          const currentCachedData =
            useDevicesStore.getState().deviceStates[deviceId];
          const cachedTimestamp = currentCachedData?.lastUpdated
            ? typeof currentCachedData.lastUpdated === "string"
              ? new Date(currentCachedData.lastUpdated).getTime()
              : currentCachedData.lastUpdated
            : 0;
          const apiTimestamp = deviceData.lastUpdated
            ? typeof deviceData.lastUpdated === "string"
              ? new Date(deviceData.lastUpdated).getTime()
              : deviceData.lastUpdated
            : 0;

          logger.log(`[loadDataForDevice] 🔍 Timestamp comparison:`);
          logger.log(
            `[loadDataForDevice] - Cached: ${cachedTimestamp} (${new Date(
              cachedTimestamp
            ).toISOString()})`
          );
          logger.log(
            `[loadDataForDevice] - API: ${apiTimestamp} (${
              apiTimestamp
                ? new Date(apiTimestamp).toISOString()
                : "no timestamp"
            })`
          );
          logger.log(
            `[loadDataForDevice] - API newer: ${apiTimestamp > cachedTimestamp}`
          );

          // Only use API data if it's newer than what we have cached
          // This prevents overwriting fresh WebSocket updates with stale API cache
          if (apiTimestamp > cachedTimestamp) {
            logger.log(
              `[loadDataForDevice] ✅ API data is newer, using API data`
            );
            const statusStore = getStatusStore();
            const mergedCreds = (deviceData.credentials || []).map((cred) => ({
              ...cred,
              status: cred.status || statusStore[cred.id] || "Active", // Prefer cached status, fall back to statusStore
            }));
            logger.log(
              `[loadDataForDevice] 🔄 Setting ${mergedCreds.length} API credentials in UI`
            );
            logger.log(
              `[loadDataForDevice] API credentials being set:`,
              mergedCreds.map((c) => ({ id: c.id, patronName: c.patronName }))
            );
            setCredentials(mergedCreds);
            updateDeviceState(deviceId, deviceData);
            // Initialize history with the cached last action
            if (deviceData.lastAction) {
              // Ensure deviceId is embedded in the action
              setActionHistory([{ ...deviceData.lastAction, deviceId }]);
            } else {
              setActionHistory([]);
            }
            logger.log(
              `[loadDataForDevice] ✅ API data applied to cache and UI`
            );
          } else {
            logger.log(
              `[loadDataForDevice] ⏭️  Cached data is newer or same, keeping cached data`
            );
            logger.log(
              `[loadDataForDevice] Cached credentials (keeping):`,
              currentCachedData?.credentials?.map((c) => ({
                id: c.id,
                patronName: c.patronName,
              })) || []
            );
            // Keep the cached data that was already displayed
          }
          setSyncing(false); // Hide modal after successful load
          // OPTION 1: Set credentialsLoaded immediately after single device fetch
          setCredentialsLoaded(true);
          logger.log(
            `[credentialsLoaded] ✅ Set to true immediately after single device fetch (Option 1)`
          );
        } else {
          logger.log(`[loadDataForDevice] ❌ No device data from API`);
          setCredentials([]);
          updateDeviceState(deviceId, null);
          setActionHistory([]);
          showNotification(
            `Could not load data for device ${deviceId}.`,
            "error"
          );
          setSyncing(false); // Hide modal on error
          const elapsedTime =
            Date.now() - (credentialsLoadStartTime.current || 0);
          const remainingTime = Math.max(500 - elapsedTime, 0);
          setTimeout(() => {
            setCredentialsLoaded(true); // Mark as loaded even on error
          }, remainingTime);
        }
        logger.log(`=== [loadDataForDevice] END ===`);
      } catch (error) {
        logger.error(`[loadDataForDevice] ❌ Error:`, error);
        showNotification(`Error loading data: ${error.message}`, "error");
        setCredentials([]);
        setDeviceStates((prevStates) => ({ ...prevStates, [deviceId]: null }));
        setActionHistory([]);
        setSyncing(false); // Hide modal if initial fetch fails
        const elapsedTime =
          Date.now() - (credentialsLoadStartTime.current || 0);
        const remainingTime = Math.max(500 - elapsedTime, 0);
        setTimeout(() => {
          setCredentialsLoaded(true); // Mark as loaded even on error
        }, remainingTime);
        logger.log(`=== [loadDataForDevice] END (error) ===`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showNotification, fetchDeviceState, getStatusStore]
    // Zustand setters (setCredentials, setCredentialsLoaded, updateDeviceState, setDeviceStates, setSyncing)
    // and setActionHistory are intentionally excluded - they're stable and don't need to trigger re-creation
  );

  /**
   * Trigger installation-wide device sync with caching
   * Checks cache validity before syncing unless force is true
   *
   * @param {string} reason - Reason for triggering sync (for logging)
   * @param {boolean} forceSync - Force sync even if cache is valid
   * @param {boolean} stateOnly - Only sync device state, not credentials
   */
  const triggerInstallationSync = useCallback(
    async (reason, forceSync = false, stateOnly = false) => {
      if (!installationId) {
        logger.warn(
          `[triggerInstallationSync] No installationId available, skipping sync`
        );
        return;
      }

      // Check cache unless forceSync is true
      if (!forceSync) {
        const cache = getInstallationSyncCache(installationId);
        if (cache && cache.isValid) {
          logger.log(
            `[triggerInstallationSync] ⏭️  Skipping sync (cache valid, age: ${Math.round(
              cache.age / 1000
            )}s) - Reason: ${reason}`
          );
          return;
        }
      }

      logger.log(
        `[triggerInstallationSync] 🔄 Triggering sync - Reason: ${reason}, Force: ${forceSync}, StateOnly: ${stateOnly}`
      );

      try {
        const response = await authenticatedFetch(
          `${config.apiEndpoint}/refresh-data`,
          {
            method: "POST",
            headers: COMMON_HEADERS,
            body: JSON.stringify({ installationId, stateOnly }),
          }
        );

        if (!response.ok) {
          throw new Error(`Sync request failed: ${response.status}`);
        }

        // FIX #1: Do NOT update cache here - cache should only be marked valid
        // AFTER the WebSocket dataRefreshed:all event confirms sync completion
        // setInstallationSyncCache(installationId); ← REMOVED

        logger.log(
          `[triggerInstallationSync] ✅ Sync triggered successfully - Reason: ${reason}`
        );
      } catch (error) {
        logger.error(
          `[triggerInstallationSync] ❌ Sync failed - Reason: ${reason}`,
          error
        );
        // Don't throw - sync failures shouldn't block UI
      }
    },
    [installationId, authenticatedFetch]
  );

  /**
   * Load initial device list on mount
   * Filters devices by installation ID and sets initial selection
   */
  const loadInitialDevices = useCallback(async () => {
    setLoading(true);
    setConfigError(null);
    try {
      logger.log("[loadInitialDevices] Fetching device list...");

      // Use fetchDevices to get basic device list (fast)
      const allDevices = await fetchDevices();

      // Filter devices by the selected installationId
      const deviceList = allDevices.filter(
        (device) => device.installationId === installationId
      );

      logger.log(
        `[loadInitialDevices] Found ${deviceList.length} devices for installation ${installationId}`
      );

      if (deviceList && deviceList.length > 0) {
        setDevices(deviceList);

        // Don't trigger automatic sync on page load
        // Installation-wide data will be lazy-loaded when needed (modals, etc.)
        logger.log(
          `[loadInitialDevices] ⏭️  Skipping automatic sync (Option 2: lazy loading enabled)`
        );

        const initialDeviceId =
          selectedDeviceIdRef.current &&
          deviceList.some((d) => d.deviceId === selectedDeviceIdRef.current)
            ? selectedDeviceIdRef.current
            : deviceList[0].deviceId;
        setSelectedDeviceId(initialDeviceId);
      } else {
        setDevices([]);
        setSelectedDeviceId("");
        setDeviceStates({});
        setCredentials([]);
        setActionHistory([]);
      }
    } catch (error) {
      if (error.message.includes("An app configuration issue")) {
        setConfigError(error.message);
      } else {
        logger.error("Error loading initial devices:", error);
        setConfigError("An unexpected error occurred loading devices.");
      }
      setDevices([]);
      setSelectedDeviceId("");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationId, fetchDevices, selectedDeviceIdRef]);
  // Zustand setters (setLoading, setDevices, setSelectedDeviceId, setDeviceStates, setCredentials, setActionHistory)
  // and setConfigError are intentionally excluded - they're stable and don't need to trigger re-creation

  return {
    loadDataForDevice,
    triggerInstallationSync,
    loadInitialDevices,
    credentialsLoadStartTime,
  };
};
